import config from 'config';
import { deleteCookie, getCookie, responseError, responseErrorWithMessage } from 'helpers';
import { APIResponse } from 'types/Request';
import {
  StaffConsultorioRequest,
  StaffMultipleRequest,
  StaffConsultorio,
  StaffBajaConsultorioRequest,
} from 'types/Staff';

function revokeCredentials() {
  deleteCookie('accessToken');
  deleteCookie('sessionCookie');
  // eslint-disable-next-line no-restricted-globals
  return location.reload();
}

export async function getAllSolicitudesStaffMultiple(
  matricula: string | null = null,
): Promise<StaffMultipleRequest[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/staff-multiple/solicitudes`);

    type Response = APIResponse<StaffMultipleRequest[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getStaffMultipleRequest(staffId: number): Promise<StaffMultipleRequest | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/staff-multiple-request/${ staffId }`);

    type Response = APIResponse<StaffMultipleRequest>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
  }
}

export async function postConsultorioStaffMultiple(
  data: Partial<StaffMultipleRequest>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff-multiple`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de staffs de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioStaffMultiple(
  data: Partial<StaffMultipleRequest>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff-multiple/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesStaff(matricula: string | null = null): Promise<StaffConsultorioRequest[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/staff/solicitudes`);

    type Response = APIResponse<StaffConsultorioRequest[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getStaffRequest(staffId: number): Promise<StaffConsultorioRequest | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/staff-request/${ staffId }`);

    type Response = APIResponse<StaffConsultorioRequest>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
  }
}

export async function postConsultorioStaff(
  data: Partial<StaffConsultorioRequest>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de staffs de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioStaff(
  data: Partial<StaffConsultorioRequest>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllStaffConsultorio(
  consultorio: number,
): Promise<StaffConsultorio[] | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/staff/${ consultorio }/all`);

    type Response = APIResponse<StaffConsultorio[]>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getAllSolicitudesStaffBaja(
  matricula: string | null = null,
): Promise<StaffBajaConsultorioRequest[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/staff/baja/solicitudes`);

    type Response = APIResponse<StaffBajaConsultorioRequest[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getStaffBajaRequest(staffId: number): Promise<StaffBajaConsultorioRequest | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/staff-baja-request/${ staffId }`);

    type Response = APIResponse<StaffBajaConsultorioRequest>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de staff.');
  }
}

export async function postConsultorioStaffBaja(
  data: Partial<StaffBajaConsultorioRequest>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff/baja`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de staffs de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioStaffBaja(
  data: Partial<StaffBajaConsultorioRequest>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/staff/baja/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de staff consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}
